<script setup>
import { ref, onMounted } from 'vue'

const currentPath = ref('/var/www/html/')
const files = ref([])
const loading = ref(false)
const uploading = ref(false)
const selectedFiles = ref([])
const showCreateFolder = ref(false)
const newFolderName = ref('')

// File upload
const fileInput = ref(null)
const uploadQueue = ref([])

// Local file browser
const showLocalBrowser = ref(false)
const localDirectory = ref('/Users/argoerit/www/oddscomposer_versions.io/oddscomposer.io_v2_29.05.2025/dist')
const localFiles = ref([])
const loadingLocalFiles = ref(false)
const selectedLocalFiles = ref([])

const loadFiles = async () => {
  loading.value = true
  try {
    // This would call your backend API to list server files
    // For now, showing placeholder data
    files.value = [
      { name: '..', type: 'directory', size: '-', modified: '2025-01-01 10:30', permissions: 'drwxr-xr-x' },
      { name: 'public', type: 'directory', size: '-', modified: '2025-01-01 09:15', permissions: 'drwxr-xr-x' },
      { name: 'storage', type: 'directory', size: '-', modified: '2025-01-01 08:45', permissions: 'drwxr-xr-x' },
      { name: 'config', type: 'directory', size: '-', modified: '2024-12-31 16:20', permissions: 'drwxr-xr-x' },
      { name: 'logs', type: 'directory', size: '-', modified: '2025-01-01 11:00', permissions: 'drwxr-xr-x' },
      { name: 'index.php', type: 'file', size: '2.1 KB', modified: '2024-12-30 14:25', permissions: '-rw-r--r--' },
      { name: 'composer.json', type: 'file', size: '1.8 KB', modified: '2024-12-29 16:10', permissions: '-rw-r--r--' },
      { name: 'app.log', type: 'file', size: '15.3 MB', modified: '2025-01-01 11:45', permissions: '-rw-r--r--' },
      { name: 'backup.sql', type: 'file', size: '45.7 MB', modified: '2024-12-31 23:59', permissions: '-rw-r--r--' }
    ]
  } catch (error) {
    console.error('Failed to load files:', error)
  } finally {
    loading.value = false
  }
}

const navigateToPath = (fileName) => {
  if (fileName === '..') {
    const parts = currentPath.value.split('/').filter(Boolean)
    parts.pop()
    currentPath.value = '/' + parts.join('/') + '/'
  } else {
    currentPath.value = currentPath.value + fileName + '/'
  }
  loadFiles()
}

const selectFile = (file) => {
  const index = selectedFiles.value.findIndex(f => f.name === file.name)
  if (index > -1) {
    selectedFiles.value.splice(index, 1)
  } else {
    selectedFiles.value.push(file)
  }
}

const selectAll = () => {
  if (selectedFiles.value.length === files.value.length) {
    selectedFiles.value = []
  } else {
    selectedFiles.value = [...files.value]
  }
}

const deleteSelected = () => {
  if (selectedFiles.value.length > 0) {
    const fileNames = selectedFiles.value.map(f => f.name).join(', ')
    if (confirm(`Are you sure you want to delete ${selectedFiles.value.length} file(s): ${fileNames}?`)) {
      // This would call backend API to delete files
      console.log('Deleting files:', selectedFiles.value)
      selectedFiles.value = []
      loadFiles()
    }
  }
}

const triggerFileUpload = () => {
  showLocalBrowser.value = true
  loadLocalFiles()
}

const triggerDirectFileUpload = async () => {
  // Try to use File System Access API (Chrome/Edge) to start from default directory
  if ('showOpenFilePicker' in window) {
    try {
      const fileHandles = await window.showOpenFilePicker({
        multiple: true,
        startIn: 'documents', // Closest we can get to custom path
        suggestedName: 'dist'
      })
      
      const files = await Promise.all(
        fileHandles.map(async handle => await handle.getFile())
      )
      
      // Add files to upload queue
      files.forEach(file => {
        uploadQueue.value.push({
          file,
          name: file.name,
          size: formatFileSize(file.size),
          progress: 0,
          status: 'pending'
        })
      })
      
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('File picker error:', error)
        // Fallback to traditional file input
        fileInput.value?.click()
      }
    }
  } else {
    // Fallback for browsers that don't support File System Access API
    fileInput.value?.click()
  }
}

const handleFileSelect = (event) => {
  const files = Array.from(event.target.files)
  files.forEach(file => {
    uploadQueue.value.push({
      file,
      name: file.name,
      size: formatFileSize(file.size),
      progress: 0,
      status: 'pending' // pending, uploading, completed, error
    })
  })
  // Clear the input
  event.target.value = ''
}

const uploadFiles = async () => {
  uploading.value = true
  
  for (const item of uploadQueue.value) {
    if (item.status === 'pending') {
      item.status = 'uploading'
      
      try {
        // Simulate upload progress
        for (let progress = 0; progress <= 100; progress += 10) {
          item.progress = progress
          await new Promise(resolve => setTimeout(resolve, 100))
        }
        
        item.status = 'completed'
        
        // This would be actual upload to backend:
        // const formData = new FormData()
        // formData.append('file', item.file)
        // formData.append('path', currentPath.value)
        // await axios.post('/api/files/upload', formData, {
        //   onUploadProgress: (progressEvent) => {
        //     item.progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
        //   }
        // })
        
      } catch (error) {
        item.status = 'error'
        console.error('Upload failed:', error)
      }
    }
  }
  
  uploading.value = false
  loadFiles() // Refresh file list
}

const clearUploadQueue = () => {
  uploadQueue.value = []
}

const createFolder = () => {
  if (newFolderName.value.trim()) {
    // This would call backend API to create folder
    console.log('Creating folder:', newFolderName.value)
    newFolderName.value = ''
    showCreateFolder.value = false
    loadFiles()
  }
}

const downloadFile = (file) => {
  // This would call backend API to download file
  console.log('Downloading file:', file.name)
}

const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

const loadLocalFiles = async () => {
  loadingLocalFiles.value = true
  try {
    // This would call your backend API to list local directory files
    // For now, showing placeholder dist folder files
    localFiles.value = [
      { name: 'index.html', type: 'file', size: '2.1 KB', modified: '2025-01-01 14:25', path: localDirectory.value + '/index.html' },
      { name: 'assets', type: 'directory', size: '-', modified: '2025-01-01 13:15', path: localDirectory.value + '/assets' },
      { name: 'css', type: 'directory', size: '-', modified: '2025-01-01 12:45', path: localDirectory.value + '/css' },
      { name: 'js', type: 'directory', size: '-', modified: '2025-01-01 11:30', path: localDirectory.value + '/js' },
      { name: 'app.js', type: 'file', size: '145.2 KB', modified: '2025-01-01 14:20', path: localDirectory.value + '/app.js' },
      { name: 'app.css', type: 'file', size: '32.1 KB', modified: '2025-01-01 14:18', path: localDirectory.value + '/app.css' },
      { name: 'favicon.ico', type: 'file', size: '4.2 KB', modified: '2024-12-30 16:10', path: localDirectory.value + '/favicon.ico' },
      { name: 'manifest.json', type: 'file', size: '512 B', modified: '2024-12-29 15:05', path: localDirectory.value + '/manifest.json' }
    ]
  } catch (error) {
    console.error('Failed to load local files:', error)
  } finally {
    loadingLocalFiles.value = false
  }
}

const selectLocalFile = (file) => {
  const index = selectedLocalFiles.value.findIndex(f => f.name === file.name)
  if (index > -1) {
    selectedLocalFiles.value.splice(index, 1)
  } else {
    selectedLocalFiles.value.push(file)
  }
}

const selectAllLocalFiles = () => {
  const selectableFiles = localFiles.value.filter(f => f.type === 'file')
  if (selectedLocalFiles.value.length === selectableFiles.length) {
    selectedLocalFiles.value = []
  } else {
    selectedLocalFiles.value = [...selectableFiles]
  }
}

const addSelectedFilesToQueue = () => {
  selectedLocalFiles.value.forEach(file => {
    // Create a mock File object for the queue
    const mockFile = {
      name: file.name,
      size: file.size,
      type: file.type,
      lastModified: new Date(file.modified).getTime()
    }
    
    uploadQueue.value.push({
      file: mockFile,
      name: file.name,
      size: file.size,
      progress: 0,
      status: 'pending',
      localPath: file.path
    })
  })
  
  selectedLocalFiles.value = []
  showLocalBrowser.value = false
}

const getFileIcon = (file) => {
  if (file.type === 'directory') return 'fas fa-folder text-yellow-500'
  
  const ext = file.name.split('.').pop()?.toLowerCase()
  const iconMap = {
    'php': 'fab fa-php text-purple-500',
    'js': 'fab fa-js-square text-yellow-500',
    'json': 'fas fa-code text-orange-500',
    'html': 'fab fa-html5 text-red-500',
    'css': 'fab fa-css3-alt text-blue-500',
    'sql': 'fas fa-database text-blue-600',
    'log': 'fas fa-file-alt text-gray-500',
    'txt': 'fas fa-file-alt text-gray-400',
    'md': 'fab fa-markdown text-gray-600',
    'pdf': 'fas fa-file-pdf text-red-600',
    'zip': 'fas fa-file-archive text-purple-600'
  }
  
  return iconMap[ext] || 'fas fa-file text-gray-400'
}

onMounted(() => {
  loadFiles()
})
</script>

<template>
  <div class="max-w-full h-full flex flex-col">
    <div class="mb-6">
      <h1 class="text-2xl font-bold text-gray-900">Server File Manager</h1>
      <p class="text-gray-600">Manage files directly on the server and upload from your computer</p>
    </div>

    <!-- Current Path & Actions -->
    <div class="bg-white rounded-lg shadow-sm border p-4 mb-4">
      <div class="flex items-center justify-between mb-3">
        <div class="flex items-center space-x-2">
          <i class="fas fa-server text-green-600"></i>
          <span class="text-sm font-medium text-gray-700">Current Path:</span>
          <span class="text-sm font-mono bg-gray-100 px-2 py-1 rounded">{{ currentPath }}</span>
        </div>
        <div class="flex items-center space-x-2">
          <button @click="loadFiles" class="px-3 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 text-sm">
            <i class="fas fa-sync-alt mr-1"></i>
            Refresh
          </button>
          <button @click="showCreateFolder = true" class="px-3 py-1 bg-green-500 text-white rounded hover:bg-green-600 text-sm">
            <i class="fas fa-folder-plus mr-1"></i>
            New Folder
          </button>
          <div class="relative">
            <button @click="triggerFileUpload" class="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm">
              <i class="fas fa-upload mr-1"></i>
              Upload from Dist
            </button>
            <button @click="triggerDirectFileUpload" 
                    class="ml-1 px-3 py-1 bg-purple-500 text-white rounded hover:bg-purple-600 text-sm"
                    :title="`Default directory: ${localDirectory}`">
              <i class="fas fa-file-upload mr-1"></i>
              Browse Computer
            </button>
          </div>
          <div class="text-xs text-gray-500 mt-1">
            <i class="fas fa-info-circle mr-1"></i>
            Default upload directory: <span class="font-mono">{{ localDirectory }}</span>
          </div>
        </div>
      </div>
      
      <!-- Selection Controls -->
      <div v-if="files.length > 0" class="flex items-center justify-between">
        <div class="flex items-center space-x-3">
          <button @click="selectAll" class="text-sm text-blue-600 hover:text-blue-800">
            {{ selectedFiles.length === files.length ? 'Deselect All' : 'Select All' }}
          </button>
          <span class="text-sm text-gray-500">{{ selectedFiles.length }} selected</span>
        </div>
        <div v-if="selectedFiles.length > 0" class="flex items-center space-x-2">
          <button @click="deleteSelected" class="px-2 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 text-sm">
            <i class="fas fa-trash mr-1"></i>
            Delete ({{ selectedFiles.length }})
          </button>
        </div>
      </div>
    </div>

    <!-- File List -->
    <div class="bg-white rounded-lg shadow-sm border flex-1 flex flex-col">
      <div class="p-3 border-b border-gray-200 bg-gray-50">
        <div class="grid grid-cols-12 gap-3 text-xs font-medium text-gray-600">
          <div class="col-span-1">
            <input type="checkbox" 
                   :checked="selectedFiles.length === files.length && files.length > 0"
                   @change="selectAll"
                   class="rounded">
          </div>
          <div class="col-span-5">Name</div>
          <div class="col-span-2">Size</div>
          <div class="col-span-2">Modified</div>
          <div class="col-span-2">Actions</div>
        </div>
      </div>

      <div class="flex-1 overflow-y-auto">
        <div v-if="loading" class="text-center py-12">
          <i class="fas fa-spinner fa-spin text-gray-400 text-2xl"></i>
          <p class="text-gray-500 mt-2">Loading files...</p>
        </div>

        <div v-else-if="files.length === 0" class="text-center py-12 text-gray-500">
          <i class="fas fa-folder-open text-4xl mb-3"></i>
          <p>No files found in this directory</p>
        </div>

        <div v-else class="p-2">
          <div v-for="file in files" :key="file.name" 
               class="grid grid-cols-12 gap-3 py-2 px-2 hover:bg-gray-50 rounded cursor-pointer text-sm"
               :class="{ 'bg-blue-50': selectedFiles.some(f => f.name === file.name) }">
            <div class="col-span-1">
              <input type="checkbox" 
                     :checked="selectedFiles.some(f => f.name === file.name)"
                     @change="selectFile(file)"
                     class="rounded">
            </div>
            <div class="col-span-5 flex items-center"
                 @click="file.type === 'directory' ? navigateToPath(file.name) : null">
              <i :class="getFileIcon(file)" class="mr-2"></i>
              <span class="truncate">{{ file.name }}</span>
            </div>
            <div class="col-span-2 text-gray-600">{{ file.size }}</div>
            <div class="col-span-2 text-gray-600 text-xs">{{ file.modified }}</div>
            <div class="col-span-2">
              <div class="flex items-center space-x-1">
                <button v-if="file.type === 'file'" 
                        @click="downloadFile(file)"
                        class="px-2 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200 text-xs">
                  <i class="fas fa-download"></i>
                </button>
                <button class="px-2 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 text-xs">
                  <i class="fas fa-trash"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Upload Queue -->
    <div v-if="uploadQueue.length > 0" class="mt-4 bg-white rounded-lg shadow-sm border">
      <div class="p-3 border-b border-gray-200 bg-blue-50">
        <div class="flex items-center justify-between">
          <h3 class="font-medium text-blue-800">Upload Queue ({{ uploadQueue.length }} files)</h3>
          <div class="flex space-x-2">
            <button @click="uploadFiles" 
                    :disabled="uploading || uploadQueue.every(item => item.status === 'completed')"
                    class="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50 text-sm">
              <i class="fas fa-cloud-upload-alt mr-1"></i>
              {{ uploading ? 'Uploading...' : 'Start Upload' }}
            </button>
            <button @click="clearUploadQueue" 
                    :disabled="uploading"
                    class="px-3 py-1 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:opacity-50 text-sm">
              <i class="fas fa-times mr-1"></i>
              Clear
            </button>
          </div>
        </div>
      </div>
      
      <div class="p-3 max-h-40 overflow-y-auto">
        <div v-for="item in uploadQueue" :key="item.name" class="mb-2">
          <div class="flex items-center justify-between text-sm">
            <div class="flex items-center">
              <i class="fas fa-file mr-2 text-gray-400"></i>
              <span>{{ item.name }}</span>
              <span class="ml-2 text-gray-500">({{ item.size }})</span>
            </div>
            <div class="flex items-center space-x-2">
              <span :class="{
                'text-gray-500': item.status === 'pending',
                'text-blue-500': item.status === 'uploading',
                'text-green-500': item.status === 'completed',
                'text-red-500': item.status === 'error'
              }" class="text-xs font-medium">
                {{ item.status === 'pending' ? 'Pending' : 
                   item.status === 'uploading' ? `${item.progress}%` :
                   item.status === 'completed' ? 'Completed' : 'Error' }}
              </span>
              <i :class="{
                'fas fa-clock text-gray-400': item.status === 'pending',
                'fas fa-spinner fa-spin text-blue-500': item.status === 'uploading',
                'fas fa-check text-green-500': item.status === 'completed',
                'fas fa-exclamation-triangle text-red-500': item.status === 'error'
              }"></i>
            </div>
          </div>
          <div v-if="item.status === 'uploading'" class="w-full bg-gray-200 rounded-full h-1 mt-1">
            <div class="bg-blue-500 h-1 rounded-full transition-all duration-300" 
                 :style="`width: ${item.progress}%`"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- Hidden file input -->
    <input ref="fileInput" 
           type="file" 
           multiple 
           @change="handleFileSelect"
           class="hidden">

    <!-- Create Folder Modal -->
    <div v-if="showCreateFolder" class="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
      <div class="bg-white rounded-lg p-6 w-96">
        <h3 class="text-lg font-medium mb-4">Create New Folder</h3>
        <input v-model="newFolderName" 
               type="text" 
               placeholder="Folder name"
               class="w-full border border-gray-300 rounded-md px-3 py-2 mb-4"
               @keyup.enter="createFolder">
        <div class="flex justify-end space-x-2">
          <button @click="showCreateFolder = false" 
                  class="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400">
            Cancel
          </button>
          <button @click="createFolder" 
                  :disabled="!newFolderName.trim()"
                  class="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 disabled:opacity-50">
            Create
          </button>
        </div>
      </div>
    </div>

    <!-- Local File Browser Modal -->
    <div v-if="showLocalBrowser" class="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div class="bg-white rounded-lg w-full max-w-4xl h-3/4 flex flex-col">
        <div class="p-4 border-b border-gray-200">
          <div class="flex items-center justify-between">
            <div>
              <h3 class="text-lg font-medium">Select Files from Local Directory</h3>
              <p class="text-sm text-gray-600 font-mono">{{ localDirectory }}</p>
            </div>
            <button @click="showLocalBrowser = false" 
                    class="text-gray-500 hover:text-gray-700">
              <i class="fas fa-times text-xl"></i>
            </button>
          </div>
        </div>

        <div class="flex-1 overflow-hidden flex flex-col">
          <div class="p-3 border-b border-gray-200 bg-gray-50">
            <div class="flex items-center justify-between">
              <div class="flex items-center space-x-3">
                <button @click="selectAllLocalFiles" class="text-sm text-blue-600 hover:text-blue-800">
                  {{ selectedLocalFiles.length === localFiles.filter(f => f.type === 'file').length ? 'Deselect All' : 'Select All Files' }}
                </button>
                <span class="text-sm text-gray-500">{{ selectedLocalFiles.length }} selected</span>
              </div>
              <button @click="loadLocalFiles" 
                      :disabled="loadingLocalFiles"
                      class="px-3 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 disabled:opacity-50 text-sm">
                <i class="fas fa-sync-alt mr-1" :class="{ 'fa-spin': loadingLocalFiles }"></i>
                Refresh
              </button>
            </div>
          </div>

          <div class="flex-1 overflow-y-auto">
            <div v-if="loadingLocalFiles" class="text-center py-12">
              <i class="fas fa-spinner fa-spin text-gray-400 text-2xl"></i>
              <p class="text-gray-500 mt-2">Loading local files...</p>
            </div>

            <div v-else-if="localFiles.length === 0" class="text-center py-12 text-gray-500">
              <i class="fas fa-folder-open text-4xl mb-3"></i>
              <p>No files found in directory</p>
            </div>

            <div v-else class="p-3">
              <div class="grid grid-cols-12 gap-3 py-2 px-2 text-xs font-medium text-gray-600 border-b">
                <div class="col-span-1">Select</div>
                <div class="col-span-6">Name</div>
                <div class="col-span-2">Type</div>
                <div class="col-span-2">Size</div>
                <div class="col-span-1">Modified</div>
              </div>
              
              <div v-for="file in localFiles" :key="file.name" 
                   class="grid grid-cols-12 gap-3 py-2 px-2 hover:bg-gray-50 rounded text-sm"
                   :class="{ 'bg-blue-50': selectedLocalFiles.some(f => f.name === file.name) }">
                <div class="col-span-1">
                  <input v-if="file.type === 'file'"
                         type="checkbox" 
                         :checked="selectedLocalFiles.some(f => f.name === file.name)"
                         @change="selectLocalFile(file)"
                         class="rounded">
                  <span v-else class="text-gray-400">-</span>
                </div>
                <div class="col-span-6 flex items-center">
                  <i :class="getFileIcon(file)" class="mr-2"></i>
                  <span class="truncate">{{ file.name }}</span>
                </div>
                <div class="col-span-2 text-gray-600">{{ file.type }}</div>
                <div class="col-span-2 text-gray-600">{{ file.size }}</div>
                <div class="col-span-1 text-gray-600 text-xs">{{ file.modified.split(' ')[0] }}</div>
              </div>
            </div>
          </div>
        </div>

        <div class="p-4 border-t border-gray-200 bg-gray-50">
          <div class="flex justify-between items-center">
            <div class="text-sm text-gray-600">
              <strong>Note:</strong> This requires a backend API to read local files. 
              Currently showing placeholder data from the dist folder.
            </div>
            <div class="flex space-x-2">
              <button @click="showLocalBrowser = false" 
                      class="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400">
                Cancel
              </button>
              <button @click="addSelectedFilesToQueue" 
                      :disabled="selectedLocalFiles.length === 0"
                      class="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50">
                <i class="fas fa-plus mr-1"></i>
                Add {{ selectedLocalFiles.length }} Files to Queue
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template> 